import React, { useState, useEffect, useRef } from 'react'

import { NotificationManager } from '../../common/components/Notifications'
import { CheckboxGrid } from '../../common/components/checkbox-grid'
import { usePageEditorContext } from '../contexts/page-editor.context'
import { SUBGROUP_COLUMN } from './report-data-set.constants'
import { reportDataSetService } from './report-data-set.service'
import { pageControlBarEditService } from './page-control-bar/page-control-bar-edit.service'

const MAX_NUM_OF_FILTERS = 5
const MAX_NUM_OF_STRATA_VALUES = 10

export const SubgroupEdit = function ({ columns = [] }) {
  const pageEditorContext = usePageEditorContext() || {}
  const currentEdit = pageEditorContext.currentEdit || {}
  const block = currentEdit.block || {}
  block.props = block.props || {}

  const [subgroups, setSubgroups] = useState(getSubgroups(block))
  const [subgroupSelections, setSubgroupSelections] = useState(reportDataSetService.getSubgroupSelections(block))
  const [showStrataMissingError, setShowStrataMissingError] = useState(false)
  const [showSplitFilterMissingError, setShowSplitFilterMissingError] = useState(false)
  const dataKey = useRef(block.dataKey)

  useEffect(() => {
    // Remove any filters pertaining to columns that no longer exist in the columns array
    if (!columns.find((column) => column === SUBGROUP_COLUMN.SPLIT_FILTER)) {
      if (block && block.props && block.props.filterDefinition && Array.isArray(block.props.filterDefinition.fields)) {
        block.props.filterDefinition.fields = block.props.filterDefinition.fields.filter(({ isSplitChartFilter }) => !isSplitChartFilter)
        updateCurrentEdit({ block })
        setSubgroupSelections(reportDataSetService.getSubgroupSelections(block))
        setSubgroups(getSubgroups(block))
      }
    }
  }, [columns])

  useEffect(() => {
    if (hasSubgroupsNotInDataSet()) {
      const message = 'One or more selected subgroups is not in the dataset. Please fix the dataset or uncheck the invalid subgroup(s).'
      NotificationManager.create({ type: 'warning', timeOut: 15000, message })
    }

    // Just set on the block to be included with any changes that are saved - no unsaved changes
    updateBlockSubgroups(subgroups)
  }, [])

  useEffect(() => {
    if (block.dataKey !== dataKey.current) {
      handleSelectionChange([])
      const subgroups = getSubgroups(block)
      setSubgroups(subgroups)
      updateBlockSubgroups(subgroups)
      dataKey.current = block.dataKey
      updateCurrentEdit({ block, removedSubgroupCache: undefined })
    }
  }, [block.dataKey])

  useEffect(() => {
    // Update subgroups when labels are pasted
    const latestSubgroups = getSubgroups(block)
    if (latestSubgroups && subgroups && JSON.stringify(latestSubgroups) !== JSON.stringify(subgroups)) {
      setSubgroups(latestSubgroups)
      updateCurrentEdit({ removedSubgroupCache: undefined })
    }
  }, [block.subgroups])

  useEffect(() => {
    setSubgroupSelections(reportDataSetService.getSubgroupSelections(block))
    setSubgroups(getSubgroups(block))
  }, [block.props.filterDefinition, block.props.strataDefinition, block.props.sliderDefinition])

  useEffect(() => {
    // Before saving the block or previewing, validate the subgroups
    pageEditorContext.updateCurrentEdit({ validate: validateSubgroups })
  }, [subgroups, subgroupSelections, columns])

  function getSubgroups (block) {
    let subgroups = reportDataSetService.getSubgroupGridOptions(pageEditorContext.page, block, columns)

    let parentFilters = []
    if (block.props.parentBlock) {
      const filters = (block.props.parentBlock.filters || []).map(({ key }) => key)
      parentFilters = parentFilters.concat(filters)
    }

    subgroups.forEach((s) => {
      // For subgroups that are filtered by a parent - remove required restriction & prevent strata selection
      if (parentFilters.includes(s.value) && s.isRequired) {
        s.isRequired = false
        s.disabledColumns = [SUBGROUP_COLUMN.STRATA, SUBGROUP_COLUMN.PAGE_FILTER, SUBGROUP_COLUMN.SLIDER]
        s.disabledColumnMessage = {
          [SUBGROUP_COLUMN.STRATA]: 'This subgroup is being filtered by a parent block.',
          [SUBGROUP_COLUMN.PAGE_FILTER]: 'This subgroup is being filtered by a parent block.',
          [SUBGROUP_COLUMN.SLIDER]: 'This subgroup is being filtered by a parent block.'
        }
      }

      // Disable strifications with more than 10 values
      const subgroupValues = reportDataSetService.getStratificationValueOptions(pageEditorContext.page, block.dataKey, s.value)
      if (Array.isArray(subgroupValues) && subgroupValues.length > MAX_NUM_OF_STRATA_VALUES) {
        if (s.disabledColumns) {
          if (!s.disabledColumns.includes(SUBGROUP_COLUMN.STRATA)) {
            s.disabledColumns = [...s.disabledColumns, SUBGROUP_COLUMN.STRATA]
          }
        } else {
          s.disabledColumns = [SUBGROUP_COLUMN.STRATA]
        }

        s.disabledColumnMessage = {
          ...s.disabledColumnMessage,
          [SUBGROUP_COLUMN.STRATA]: `Sorry, you cannot stratify a subgroup with more than ${MAX_NUM_OF_STRATA_VALUES} legend values.`
        }
      }
    })

    return subgroups
  }

  function validateSubgroups () {
    let isValid = true
    let isStrataMissing = false
    let isSplitFilterMissing = false
    const strata = (reportDataSetService.getStrataDefinitionOptions(block) || []).map(({ value }) => value)

    // validate that at least one strata is selected
    if (columns.includes(SUBGROUP_COLUMN.STRATA) && (!strata || strata.length < 1)) {
      isStrataMissing = true
      NotificationManager.create({
        type: 'error',
        timeOut: 10000,
        message: 'You must select at least one subgroup to stratify by.'
      })
      isValid = false
    }

    if (columns.includes(SUBGROUP_COLUMN.SPLIT_FILTER) && !reportDataSetService.getSplitChartFilter(block)) {
      isSplitFilterMissing = true
      NotificationManager.create({
        type: 'error',
        timeOut: 10000,
        message: 'You must select at least one subgroup to split the chart by.'
      })
      isValid = false
    }

    // clear any previous subgroup specific errors
    const updatedSubgroups = subgroups.map((subgroup) => {
      subgroup.isError = false
      return subgroup
    })

    // validate the required subgroups (ones that do not have null value) are selected
    const selectedSubgroups = subgroupSelections.reduce((result, { row }) => {
      if (!result.includes(row)) {
        result.push(row)
      }
      return result
    }, [])
    const requiredSubgroupsUnselected = subgroups
      .filter(({ isRequired }) => isRequired)
      .filter((subgroup) => {
        subgroup.isError = !selectedSubgroups.includes(subgroup.value)
        return subgroup.isError
      })
    if (requiredSubgroupsUnselected.length > 0) {
      const message = 'You must check at least one option for all subgroups marked with asterisks.'
      NotificationManager.create({ type: 'error', timeOut: 10000, message })
      isValid = false
    }

    setShowStrataMissingError(isStrataMissing)
    setShowSplitFilterMissingError(isSplitFilterMissing)
    setSubgroups(updatedSubgroups)

    return isValid
  }

  function updateBlockSubgroups (subgroups) {
    block.subgroups = subgroups.map(({ value, label }) => ({ value, label }))
  }

  function handleSelectionChange (selections) {
    // Enforce only one strata for non-interactive pages
    if (!pageEditorContext.page.isInteractive && selections.filter((selection) => selection.column === SUBGROUP_COLUMN.STRATA).length > 1) {
      const lastStrataSelection = [...selections].reverse().find((selection) => selection.column === SUBGROUP_COLUMN.STRATA)
      selections = selections.filter((selection) => selection.column !== SUBGROUP_COLUMN.STRATA || selection === lastStrataSelection)
    }

    const { updatedBlock, removedCache } = reportDataSetService.updateStrataAndFilterDefinitions(
      pageEditorContext.page, block, selections, subgroups, currentEdit.removedSubgroupCache
    )

    // clear strata definition if strata is not a column this block supports
    if (!columns.includes(SUBGROUP_COLUMN.STRATA) && updatedBlock.props.strataDefinition) {
      updatedBlock.props.strataDefinition = undefined
    }

    const currentSliderKey = reportDataSetService.getSliderDefinitionKey(updatedBlock)
    const sliderSelection = selections.find(({ column }) => column === SUBGROUP_COLUMN.SLIDER)
    if (sliderSelection && sliderSelection.row !== currentSliderKey) {
      // slider selection has been set to a different value than the current one - update the block
      const key = sliderSelection.row
      const subgroup = subgroups.find(({ value }) => value === key) || {}
      const label = subgroup.label === undefined ? key : subgroup.label
      const values = reportDataSetService.getSubgroupValues(pageEditorContext.page, dataKey.current, key)
      updatedBlock.props.sliderDefinition = reportDataSetService.buildSliderDefinition(key, label, values)
    } else if (currentSliderKey !== undefined && !sliderSelection) {
      // slider selection has been cleared - remove it from the block
      updatedBlock.props.sliderDefinition = undefined
    }

    const filterFields = updatedBlock.props.filterDefinition && updatedBlock.props.filterDefinition.fields
    if (!pageEditorContext.page.isInteractive && filterFields.length) {
      filterFields
        .filter((field) => field.options && field.options.length && field.selectedValue === undefined)
        .forEach((field) => {
          field.selectedValue = field.options[0].value
          field.options[0].hide = false
          for (let i = 1; i < field.options.length; i++) {
            field.options[i].hide = true
          }
        })
    }

    const pageControlBarUpdates = updatePageControlBar(updatedBlock, selections)
    const currentEditUpdates = {
      block: updatedBlock,
      removedSubgroupCache: removedCache,
      ...pageControlBarUpdates
    }

    setSubgroupSelections(selections)
    updateCurrentEdit(currentEditUpdates)
    setSubgroups(getSubgroups(currentEditUpdates.block))
  }

  function updatePageControlBar (block, selections) {
    const page = pageEditorContext.page
    let pageControlBar = pageControlBarEditService.getPageControlBar(page, currentEdit)

    const result = pageControlBarEditService.updatePageControlBar(pageControlBar, page, block, selections)

    const currentEditUpdates = {
      block: result.block,
      pageControlBar: result.pageControlBar
    }

    // If page control bar exists on the page and current updates have removed it - mark it to be removed on the next save
    currentEditUpdates.shouldRemovePageControlBar = (page.layout.pageControlBar !== undefined && result.updatedPageControlBar === undefined)

    return currentEditUpdates
  }

  function handleSubgroupsChange (subgroups) {
    reportDataSetService.updateBlockFromSubgroupGridOptions(block, subgroups)
    updateCurrentEdit({ block })
    setSubgroups(subgroups)
  }

  function hasSubgroupsNotInDataSet () {
    const validValues = subgroups.map(({ value }) => value)
    return subgroupSelections.find(({ row }) => !validValues.includes(row))
  }

  function updateCurrentEdit (currentEdit) {
    // Ensure any updates to the block result in a new object
    if (currentEdit.block) {
      currentEdit.block = { ...currentEdit.block }
    }

    pageEditorContext.registerUnsavedChanges()
    pageEditorContext.updateCurrentEdit(currentEdit)
  }

  function buildColumnList () {
    return columns.map((column) => {
      const result = { value: column, label: column }
      if (column === SUBGROUP_COLUMN.STRATA) {
        result.isRequired = true
        result.isError = showStrataMissingError
      }
      if (pageEditorContext.page.isInteractive && [SUBGROUP_COLUMN.SLIDER, SUBGROUP_COLUMN.PRIMARY, SUBGROUP_COLUMN.SPLIT_FILTER].includes(column)) {
        result.allowMulti = false
      }
      if (column === SUBGROUP_COLUMN.FILTERS) {
        result.maxSelectionCount = MAX_NUM_OF_FILTERS
        result.maxSelectionMessage = `Sorry, you have reached the limit of ${MAX_NUM_OF_FILTERS} filters.`

        // If this is a non-interactive page, rename from 'Other Filters' to 'Filters'
        if (!pageEditorContext.page.isInteractive) {
          result.label = 'Filters'
        }
      }
      if (column === SUBGROUP_COLUMN.SPLIT_FILTER) {
        result.isRequired = true
        result.isError = showSplitFilterMissingError
      }
      return result
    })
  }

  if (subgroups.length === 0) {
    return null
  }

  return (
    <CheckboxGrid
      label="Subgroups"
      labelSize="medium"
      rows={subgroups}
      onRowsChange={handleSubgroupsChange}
      columns={buildColumnList()}
      selections={subgroupSelections}
      onSelectionChange={handleSelectionChange}
    />
  )
}
